import { useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
    MarkEmailRead,
    ScheduleSend,
    MarkChatRead,
    ThumbDown,
} from '@mui/icons-material';
import * as XLSX from 'xlsx'
import { getAllInvitations } from '../../../../../../app/store/slices/event/invitation'
import { capitalizeString } from '../../../../../../utils/strings/capitilize'
import { formatToYYYYMMDDHHMM } from '../../../../../../utils/dateFormatter'
import { errorToast } from '../../../../../../utils/toasts/toasts'

export default function useResponseManage() {
    const dispatch = useDispatch()
    const { invitations } = useSelector((state) => state.invitation);
    const { eventData } = useSelector((state) => state.event);

    const [openExportModal, setOpenExportModal] = useState(false)
    const [exportLoading, setExportLoading] = useState(false)
    const [responseType, setResponseType] = useState(null);


    /**
     * @function to export invitations data to an Excel file
     * @param {Array} invitations - The array of invitation objects
     * @param {string} fileName - The name of the file to be saved (default is 'data')
     * @returns {void}
     */
    function exportToExcel(invitations, fileName = 'data') {
        const worksheet = XLSX.utils.json_to_sheet(invitations);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Invitations');
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
    }


    /**
     * @function to filter and format invitations data based on response type
     * @param {Array} invitations - The array of invitation objects
     * @param {string} responseType - The type of response to filter by ('pending', 'yes', 'no', or '')
     * @returns {Array} - The filtered and formatted array of invitation objects
     */
    function filterInvitationsData(invitations, responseType = '') {
        return invitations.map((invitation, index) => {
            const formattedResponse = capitalizeString(invitation.response);
            return {
                No: index + 1,
                Name: capitalizeString(invitation.name, true),
                Email: invitation.email,
                Phone: invitation.phone,
                Response: formattedResponse,
                'Responded At': formattedResponse === 'pending' ? 'N/A' : formatToYYYYMMDDHHMM(invitation.updated_at),
                Registered: invitation.registered ? 'Yes' : 'No'
            };
        }).filter(invitation => {
            if (responseType === 'pending') return invitation.Response === 'Pending';
            if (responseType === 'yes') return invitation.Response === 'Yes';
            if (responseType === 'no') return invitation.Response === 'No';
            return true; // Default is no filter (all invitations)
        });
    }


    /**
     * @async function to handle exporting invitations based on response type
     * @param {string} responseType - The type of response to filter by
     * @returns {void}
     */
    async function exportResponses(responseType) {
        try {
            setExportLoading(true);
            setResponseType(responseType)

            const allInvitations = await dispatch(getAllInvitations(eventData?.eventId)).unwrap();

            const invitationsData = filterInvitationsData(allInvitations?.data, responseType);

            if (invitationsData?.length === 0) {
                errorToast('No invitations found for the selected response type.');
                return; // Exit if no data to export
            }

            // Set fileName based on the responseType
            let fileName = '';
            if (responseType === 'pending') {
                fileName = 'Pending_Invitations';
            } else if (responseType === 'yes') {
                fileName = 'Accepted_Invitations';
            } else if (responseType === 'no') {
                fileName = 'Rejected_Invitations';
            } else {
                fileName = 'All_Invitations';
            }

            exportToExcel(invitationsData, fileName);

        } catch (error) {
            errorToast('Failed to export invitations. Please try again.');
        } finally {
            setExportLoading(false);
            setResponseType(null)
        }
    }


    /**
     * @function to compute the number of invitations by their response status
     * @returns {Object} - An object containing counts of sent, rejected, accepted, and pending invitations
     */
    const invites = useMemo(() => {
        return {
            sent: (invitations?.length || 0),
            rejected: invitations?.filter(invite => invite.status === 'rejected').length || 0,
            accepted: invitations?.filter(invite => invite.status === 'accepted').length || 0,
            pending: invitations?.filter(invite => invite.status === 'pending').length || 0,
        }
    }, [invitations]);


    /**
     * @constant analyticsCards - An array of objects representing analytics cards for invitation statistics
     */
    const analyticsCards = [
        {
            title: 'Total Invites Sent',
            value: invites.sent,
            valueColor: 'var(--quinary-secondary-color)',
            icon: <MarkEmailRead sx={{ color: 'var(--quinary-secondary-color)' }} />,
            iconBgColor: 'var(--quinary-light-bg-color-2)'
        },
        {
            title: 'Invites Accepted',
            value: invites.accepted,
            valueColor: 'var(--success-secondary-color)',
            icon: <MarkChatRead sx={{ color: 'var(--success-secondary-color)' }} />,
            iconBgColor: 'var(--success-light-bg-color-2)'
        },
        {
            title: 'Pending Invites',
            value: invites.pending,
            valueColor: 'var(--warning-main-color)',
            icon: <ScheduleSend sx={{ color: 'var(--warning-main-color)' }} />,
            iconBgColor: 'var(--warning-light-bg-color)'
        },
        {
            title: 'Rejected Invites',
            value: invites.rejected,
            valueColor: 'var(--error-main-color)',
            icon: <ThumbDown sx={{ color: 'var(--error-main-color)' }} />,
            iconBgColor: 'var(--error-light-bg-color-2)'
        }
    ];


    /**
     * @return {Object} - An object containing state variables and functions for managing invitation responses
     */
    return {
        openExportModal,
        analyticsCards,
        invitations,
        setOpenExportModal,
        exportLoading,
        exportResponses,
        responseType
    }
}